import { useEffect, useRef } from "react";

const COLORS = {
  confetti: ["#f4b942", "#e5679a", "#5bc0be", "#9b7fc9", "#f25f5c"],
  fireworks: ["#ff5a5f", "#ffffff", "#5aa9ff", "#ffd166"],
  petals: ["#f7c6d0", "#fde2e4", "#ffffff"],
  hearts: ["#f48fb1", "#f06292", "#fce4ec"],
  snow: ["#ffffff"],
  bats: ["rgba(30,15,35,0.75)", "rgba(150,70,20,0.65)"],
};
const AMBIENT = { snow: 70, bats: 9, petals: 22, hearts: 16 };
const BURST = { confetti: 140, fireworks: 90 };

const spawn = (type, w, h, burst) => {
  const c = COLORS[type][Math.floor(Math.random() * COLORS[type].length)];
  if (type === "fireworks") {
    const a = Math.random() * Math.PI * 2;
    const s = 1.5 + Math.random() * 4;
    return { x: burst.x, y: burst.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, r: 1.6 + Math.random() * 1.6, c, life: 70 + Math.random() * 40, g: 0.035 };
  }
  if (type === "confetti") {
    return { x: Math.random() * w, y: -10 - Math.random() * h * 0.4, vx: (Math.random() - 0.5) * 2, vy: 2 + Math.random() * 3, r: 3 + Math.random() * 4, c, life: 220, rot: Math.random() * 6, vr: (Math.random() - 0.5) * 0.3, g: 0.02 };
  }
  const fall = type === "snow" ? 0.5 + Math.random() * 1.2 : type === "bats" ? -0.2 + Math.random() * 0.4 : 0.4 + Math.random() * 0.7;
  return {
    x: Math.random() * w, y: type === "bats" ? Math.random() * h * 0.6 : -10 - Math.random() * h, vx: type === "bats" ? 0.6 + Math.random() * 0.8 : (Math.random() - 0.5) * 0.6,
    vy: fall, r: type === "snow" ? 1 + Math.random() * 2.4 : 5 + Math.random() * 5, c, life: Infinity, phase: Math.random() * 6, rot: Math.random() * 6, vr: 0.01, g: 0,
  };
};

const draw = (ctx, type, p, t) => {
  ctx.fillStyle = p.c;
  ctx.save();
  ctx.translate(p.x, p.y);
  if (type === "bats") {
    const flap = Math.sin(t / 120 + p.phase) * 0.6;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(-p.r, -p.r * flap, -p.r * 2, 0);
    ctx.quadraticCurveTo(-p.r, p.r * 0.3, 0, p.r * 0.4);
    ctx.quadraticCurveTo(p.r, p.r * 0.3, p.r * 2, 0);
    ctx.quadraticCurveTo(p.r, -p.r * flap, 0, 0);
    ctx.fill();
  } else if (type === "hearts") {
    ctx.rotate(Math.sin(t / 900 + p.phase) * 0.3);
    const s = p.r / 6;
    ctx.beginPath();
    ctx.moveTo(0, 3 * s);
    ctx.bezierCurveTo(-6 * s, -2 * s, -3 * s, -7 * s, 0, -3 * s);
    ctx.bezierCurveTo(3 * s, -7 * s, 6 * s, -2 * s, 0, 3 * s);
    ctx.fill();
  } else if (type === "petals" || type === "confetti") {
    ctx.rotate(p.rot);
    if (type === "petals") { ctx.beginPath(); ctx.ellipse(0, 0, p.r, p.r * 0.55, 0, 0, Math.PI * 2); ctx.fill(); } else ctx.fillRect(-p.r / 2, -p.r / 4, p.r, p.r / 2);
  } else {
    ctx.globalAlpha = type === "fireworks" ? Math.max(0, p.life / 110) : 0.85;
    ctx.beginPath(); ctx.arc(0, 0, p.r, 0, Math.PI * 2); ctx.fill();
  }
  ctx.restore();
};

/** Lightweight canvas particles: ambient (snow/bats/petals/hearts) or bursts on each slide change (confetti/fireworks). */
export const Particles = ({ type, burstKey }) => {
  const ref = useRef(null);
  const parts = useRef([]);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !COLORS[type]) return undefined;
    const ctx = cv.getContext("2d");
    let raf;
    const fit = () => { cv.width = cv.clientWidth; cv.height = cv.clientHeight; };
    fit();
    window.addEventListener("resize", fit);
    parts.current = AMBIENT[type] ? Array.from({ length: AMBIENT[type] }, () => spawn(type, cv.width, cv.height)) : [];
    const tick = (t) => {
      ctx.clearRect(0, 0, cv.width, cv.height);
      parts.current = parts.current.filter((p) => p.life > 0 && p.y < cv.height + 40 && p.x < cv.width + 60);
      if (AMBIENT[type]) while (parts.current.length < AMBIENT[type]) parts.current.push(spawn(type, cv.width, cv.height));
      for (const p of parts.current) {
        p.vy += p.g; p.x += p.vx + (type === "snow" || type === "petals" ? Math.sin(t / 1000 + (p.phase || 0)) * 0.3 : 0); p.y += p.vy;
        p.rot = (p.rot || 0) + (p.vr || 0); p.life -= 1;
        if (type === "bats" && p.x > cv.width + 40) { p.x = -40; p.y = Math.random() * cv.height * 0.6; }
        draw(ctx, type, p, t);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", fit); };
  }, [type]);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !BURST[type] || burstKey == null) return;
    const at = { x: cv.width * (0.25 + Math.random() * 0.5), y: cv.height * (0.2 + Math.random() * 0.25) };
    for (let i = 0; i < BURST[type]; i += 1) parts.current.push(spawn(type, cv.width, cv.height, at));
  }, [burstKey, type]);
  if (!COLORS[type]) return null;
  return <canvas ref={ref} className="ss-particles" aria-hidden data-testid={`slideshow-particles-${type}`} />;
};
